import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserStoreService } from '../../store/user';
import { AuthQuery } from '../../store/auth';
import { signUp } from '@aws-amplify/auth';
import { getRegistrationForm, mapRegistrationFormDataToCognitoAttributes } from './registration.form';
import { GooglePlaceChanged, PhoneNumberComponent } from '@railmybox/ui';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'rmb-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SignUpComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(FormBuilder);
  private authQuery = inject(AuthQuery);
  private translocoService = inject(TranslocoService);
  private userStoreService = inject(UserStoreService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly TRANSLOCO_PREFIX = 'auth.signup.form';

  @ViewChild(PhoneNumberComponent) phoneNumberComponent: PhoneNumberComponent;
  registrationForm: FormGroup;
  errorMessage: string;
  defaultCountryCode = 'DE';
  readonly loading = signal(false);

  constructor() {
    this.registrationForm = getRegistrationForm(this.formBuilder);
  }

  ngOnInit() {
    if (this.route.snapshot.queryParams) {
      const nestedParams = this.nestParams(this.route.snapshot.queryParams);
      this.registrationForm.patchValue({
        ...nestedParams,
        ...nestedParams.address,
        address: nestedParams.formattedAddress,
        country: nestedParams.address?.countryCode,
      });
      this.userStoreService.updateUserState({
        invitationKey: this.route.snapshot.queryParams?.invitationKey,
      });
    }
  }

  nestParams(params: Params): any {
    const result: any = {};

    for (const key in params) {
      const value = params[key];
      const keys = key.split('.');
      let current = result;

      keys.forEach((k, index) => {
        if (index === keys.length - 1) {
          current[k] = value;
        } else {
          current[k] = current[k] || {};
          current = current[k];
        }
      });
    }

    return result;
  }

  async registration(): Promise<void> {
    this.phoneNumberComponent.updateFormValidity();
    this.registrationForm.markAllAsTouched();
    if (this.registrationForm.invalid) {
      return;
    }
    this.errorMessage = '';
    this.loading.set(true);

    const registrationFormValue = this.registrationForm.value;
    const attributes = mapRegistrationFormDataToCognitoAttributes({
      ...registrationFormValue,
      locale: this.authQuery.getUserLanguageValue(),
      defaultCountryCode: this.defaultCountryCode,
    });

    const { email, password } = registrationFormValue;

    try {
      await signUp({ username: email, password, options: { userAttributes: attributes } });

      this.userStoreService.updateUserState({
        email,
      });

      await this.router.navigate(['/auth', 'verify']);
    } catch (err) {
      this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
      this.changeDetectorRef.detectChanges();
    }
    this.loading.set(false);
  }

  addressChanged(address: string) {
    if (address) {
      return;
    }

    this.registrationForm.get('address').markAsTouched();
    this.registrationForm.patchValue({ address: null });
  }

  onPlaceChanged(placeChanged: GooglePlaceChanged) {
    delete placeChanged.companyName;

    this.registrationForm.patchValue({ ...placeChanged }, { emitEvent: false });
    this.defaultCountryCode = placeChanged.countryCode;
    this.registrationForm.get('address').markAsTouched();
    this.registrationForm.get('address').updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
  }
}
